/**
 * Paint scheduling for the html-in-canvas paint loop.
 *
 * The unified CanvasSurface paint loop used to call `canvas.requestPaint()`
 * unconditionally on every rAF — repainting every frame at display refresh even
 * when nothing changed. With N frames each
 * running effects (outline = 9 drawElementImage calls, shaders = full WebGL
 * pipeline) that idle burn is the dominant cost.
 *
 * `createPaintSignal` collapses that to: paint only when something changed.
 * Sources of "changed":
 *  - any store mutation (commands, drag, pan/zoom, selection) via store.subscribe
 *  - DOM-only interaction the store never sees: typing in inputs/contenteditable,
 *    hover/focus CSS state, scrolling inside a frame — caught as capture-phase
 *    events on the canvas element
 *  - self-animating frames (crt time shader, running CSS/WAAPI animations,
 *    playing <video>/<audio>) — kept painting via setLive() each paint
 *
 * The rAF loop stays in the component (sizing differs per path); this owns only
 * the dirty/live decision + listener wiring so it's unit-testable.
 */
import { useCanvasStore } from '../store/canvas-store';
import type { Frame, MotionTimeline } from '../store/types';

export interface PaintSignal {
  /** true if a repaint is needed this rAF tick */
  shouldPaint(): boolean;
  /** clear the one-shot dirty flag after issuing a paint */
  afterPaint(): void;
  /** mark whether any painted frame is self-animating (keeps the loop alive) */
  setLive(live: boolean): void;
  /** force a repaint on the next tick */
  markDirty(): void;
  /** detach store + DOM listeners */
  dispose(): void;
}

export interface PaintSignalOpts {
  /** element to watch for user-interaction events (typing, hover, scroll) */
  eventTarget?: HTMLElement | null;
}

/** Capture-phase events that imply visible DOM change the store doesn't track. */
const INTERACTION_EVENTS = [
  'input',
  'keydown',
  'pointerdown',
  'pointermove',
  'pointerup',
  'wheel',
  'focusin',
  'focusout',
  'scroll',
] as const;

export function createPaintSignal(opts: PaintSignalOpts = {}): PaintSignal {
  let dirty = true; // first paint always runs
  let live = false;
  const markDirty = () => {
    dirty = true;
  };

  const unsubStore = useCanvasStore.subscribe(markDirty);

  const target = opts.eventTarget ?? null;
  const listenerOpts: AddEventListenerOptions = { capture: true, passive: true };
  if (target) {
    for (const ev of INTERACTION_EVENTS) {
      target.addEventListener(ev, markDirty, listenerOpts);
    }
  }

  return {
    shouldPaint: () => dirty || live,
    afterPaint: () => {
      dirty = false;
    },
    setLive: (v: boolean) => {
      live = v;
    },
    markDirty,
    dispose: () => {
      unsubStore();
      if (target) {
        for (const ev of INTERACTION_EVENTS) {
          target.removeEventListener(ev, markDirty, { capture: true });
        }
      }
    },
  };
}

/**
 * Does this frame need continuous repainting? True when its painted pixels can
 * change without any store mutation or user interaction:
 *  - crt effect animates on a time uniform
 *  - the subtree has a running CSS/WAAPI animation or transition
 *  - it contains playing <video>/<audio>
 */
export function frameNeedsContinuousPaint(frame: Frame, el: Element | null | undefined): boolean {
  if (frame.effect?.kind === 'crt') return true; // time-driven shader

  // Motion timelines write inline styles imperatively (no WAAPI Animation),
  // so getAnimations() won't catch them. Require continuous repaint whenever
  // a frame has an active motion timeline.
  const m = 'motion' in frame ? (frame as { motion?: MotionTimeline }).motion : undefined;
  if (m && m.tracks.length > 0) return true;

  if (!el) return false;

  if (typeof el.getAnimations === 'function') {
    try {
      const anims = el.getAnimations({ subtree: true });
      if (anims.some((a) => a.playState === 'running')) return true;
    } catch {
      /* subtree option unsupported in this engine — fall through */
    }
  }

  const media = el.querySelectorAll?.('video, audio');
  if (media) {
    for (const m of Array.from(media) as HTMLMediaElement[]) {
      if (!m.paused && !m.ended && m.readyState > 2) return true;
    }
  }

  return false;
}
